import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { track } from "@/lib/analytics"
import * as oioApi from "../../oio-sim/api"
import * as oiaApi from "../../oia-sim/api"
import { useDocsStore as useOioDocs } from "../../oio-sim/docs.store"
import { useDocsStore as useOiaDocs } from "../../oia-sim/docs.store"
import { filterSimFiles, SIM_ACCEPT } from "../../oio-sim/files"
import { SimBanner } from "../../oio-sim/SimBanner"
import { startMessage as oioStart, startRequestId } from "../../oio-sim/start"
import { startMessage as oiaStart } from "../../oia-sim/start"
import { getCase, getMatterContents } from "../api"
import type { MatterContents, MatterFile, ProceedingRole } from "../contract"
import { shortDate } from "../answer/format"
import { usingLine } from "../matter/copy"
import { useProcessingPoll } from "../matter/fileStatus"
import { FilesChecklist, ProceedingRadios, ReadingWait } from "../matter/ProceedingChoice"
import { shelfOf } from "../matter/proceedings"
import { useProceedingChoice } from "../matter/useProceedingChoice"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import { Beam } from "../ds/Beam"
import { NiraBar } from "../ds/NiraBar"
import { Rail, ago } from "../answer/Rail"
import { usePageTitle, useStoredTheme } from "../answer/hooks"
import { GateLine, gateAction } from "../ds/gate"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { planStateOf, type QuotaMetric } from "@/lib/quotaGate"
import { CHIP, docParts, kindLabel, missing, pagesLabel, type DocKind, type SimKind } from "./docKinds"
import { Ladder, totalMinutes } from "./Ladder"
import { usePasteFiles } from "../../order-sim/usePasteFiles"
import { trackRefused, trackUploadFailed, trackUploaded, uploadFailReason } from "../../order-sim/simAnalytics"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../answer/pro.css"
import "../case/case.css"
import "./sim.css"

const LAST_KEY = "nira.sim.last"
const lastUsed = (): SimKind => {
  try { return localStorage.getItem(LAST_KEY) === "oia" ? "oia" : "oio" } catch { return "oio" }
}
// The case file's draft not run yet, per order: a reload goes back to it instead of making another
// and reading the documents again.
const keptKey = (caseId: string, k: SimKind) => `nira.sim.case.${caseId}.${k}`
const kept = (caseId: string, k: SimKind) => { try { return localStorage.getItem(keptKey(caseId, k)) ?? "" } catch { return "" } }
const keep = (caseId: string, k: SimKind, id: string | null) => {
  try { if (id) localStorage.setItem(keptKey(caseId, k), id); else localStorage.removeItem(keptKey(caseId, k)) } catch { /* only a reload makes another */ }
}

const SIM = {
  oio: {
    eyebrow: "Order-in-Original", title: "From a show cause notice", base: "/oio-sim", metric: "oio_sim.draft" as const,
    steps: "facts, allegations, time limit, findings, the money, the draft, checked",
    body: "Takes the SCN, the reply, the hearing record, DRC-01. Gives the adjudicating officer's order.",
    drop: "Drop every document for the OIO here",
    dropSub: "The show cause notice, the reply if one was filed, the hearing record, DRC-01, annexures. Nira tells each apart and says what is missing.",
    api: oioApi, start: oioStart,
  },
  oia: {
    eyebrow: "Order-in-Appeal", title: "From an order under appeal", base: "/oia-sim", metric: "oia_sim.draft" as const,
    steps: "the appeal, its grounds, time limit and pre-deposit, findings, the relief, the draft, checked",
    body: "Takes the OIO, the appeal (APL-01) and its grounds, the SCN and reply behind it. Gives the appellate authority's order.",
    drop: "Drop every document for the OIA here",
    dropSub: "The order appealed against, the appeal with its grounds, the notice and reply behind the order, the hearing record. Nira tells each apart and says what is missing.",
    api: oiaApi, start: oiaStart,
  },
}

/** A short reason a file was refused, from the server's status. */
export function uploadReason(err: unknown): string {
  const status = (err as { status?: number })?.status
  if (status === 413) return "too large"
  if (status === 415) return "not a supported file"
  if (status === 422) return (err as Error).message || "not accepted"
  if (status === 401 || status === 403) return "sign in again"
  return "server error, try again"
}

interface Recent { id: string; title: string; updated_at: string; kind: SimKind }
// files is null when the Matter's contents could not be loaded: the page then works as a plain start.
interface Linked { id: string; title: string; files: MatterFile[] | null; proceedings?: MatterContents["proceedings"]; unlinked?: string[] }

/** The role a workspace file plays in what the order still lacks. */
const KIND_OF_ROLE = (role: ProceedingRole, sim: SimKind): DocKind =>
  role === "scn" ? "scn" : role === "reply" ? "reply" : role === "hearing" ? "hearing" : role === "appeal" ? "apl" : role === "order" && sim === "oia" ? "oio" : "other"

/**
 * The order simulator's start page (handoff 4, Sim-Start). The order is chosen first, because an
 * appeal bundle carries the notice and often the reply too: the documents alone do not say which
 * order is wanted. Then every document in one drop, one button; Nira asks when something is missing
 * and shows each stage for the reader to confirm.
 */
export default function SimStart() {
  useStoredTheme()
  usePageTitle("Order simulator")
  const navigate = useNavigate()
  // Started from a case file (/simulator?case=…&order=oio|oia): the draft goes in it, and the
  // case file's home lists it. The order is the one the case file holds a document for.
  const [params] = useSearchParams()
  const caseParam = params.get("case") ?? ""
  const orderParam = params.get("order")
  const [kind, setKind] = useState<SimKind>(() => (orderParam === "oio" || orderParam === "oia" ? orderParam : lastUsed()))
  const [threads, setThreads] = useState<Record<SimKind, string>>({ oio: "", oia: "" })
  const [busy, setBusy] = useState<"" | "upload" | "start">("")
  const [said, setSaid] = useState("")
  const [over, setOver] = useState(false)
  const [histOpen, setHistOpen] = useState(false)
  // What the gate said when it stopped Draft the order with nothing to sell, or after "Not now".
  const [gateLine, setGateLine] = useState("")
  const [recent, setRecent] = useState<Recent[] | null>(null)
  const file = useRef<HTMLInputElement>(null)
  const dropZone = useRef<HTMLDivElement>(null)
  // Only a case file the reader can open is linked; the drop waits for the answer.
  const caseLookup = useMemo<Promise<Linked | null>>(
    () => (caseParam
      ? getCase(caseParam).then(async (c) => {
        const id = c.merged_into || c.id
        const m = await getMatterContents(id).catch(() => null)
        return { id, title: c.title, files: m ? m.files : null, proceedings: m?.proceedings, unlinked: m?.unlinked }
      }).catch(() => null)
      : Promise.resolve(null)),
    [caseParam],
  )
  const [linked, setLinked] = useState<Linked | null>(null)
  // One case per order, made once even when a drop and the case file's documents race for it.
  const making = useRef<Partial<Record<SimKind, Promise<string>>>>({})
  // The selection each draft was made with, so a changed selection before Start makes the draft again.
  const madeWith = useRef<Partial<Record<SimKind, string>>>({})
  const sim = SIM[kind]
  const threadId = threads[kind]
  const oioDocs = useOioDocs((s) => (threads.oio ? s.threads[threads.oio]?.docs : undefined))
  const oiaDocs = useOiaDocs((s) => (threads.oia ? s.threads[threads.oia]?.docs : undefined))
  const docs = useMemo(() => Object.values((kind === "oio" ? oioDocs : oiaDocs) ?? {}).filter((d) => d.filename), [kind, oioDocs, oiaDocs])
  // The draft's own uploads reach the Matter's shelf once started; they are this draft's, not a choice.
  const shelf = useMemo(() => shelfOf(linked ? {
    files: (linked.files ?? []).filter((f) => !(threadId && f.origin.session_id === threadId)),
    proceedings: linked.proceedings, unlinked: linked.unlinked,
  } : null), [linked, threadId])
  const choice = useProceedingChoice(shelf, kind)
  // Arriving from the workspace's New session menu with one proceeding already chosen.
  const proceedingParam = params.get("proceeding") ?? ""
  useEffect(() => { if (proceedingParam) choice.choose(proceedingParam) }, [proceedingParam, choice.options.length]) // eslint-disable-line react-hooks/exhaustive-deps
  const chosenFiles = useMemo(() => choice.members.filter((m) => choice.isChecked(m.file.doc_id)), [choice])
  const kinds = [
    ...docs.filter((d) => d.status === "ready").flatMap((d) => docParts(d.summary, d.filename, kind).map((p) => p.kind)),
    ...chosenFiles.map((m) => KIND_OF_ROLE(m.role, kind)),
  ]
  const gaps = missing(kinds, kind)
  // Files still being read have no role yet: nothing is called missing, and Start waits.
  const readingMatter = choice.reading.length > 0
  const reading = readingMatter || docs.some((d) => d.status !== "ready" && d.status !== "failed")
  const reload = useCallback(() => {
    if (!linked) return
    getMatterContents(linked.id).then((m) => setLinked((cur) => (cur && cur.id === linked.id ? { ...cur, files: m.files, proceedings: m.proceedings, unlinked: m.unlinked } : cur))).catch(() => undefined)
  }, [linked])
  useProcessingPoll(linked?.files ?? [], reload)
  const usable = (linked?.files ?? []).filter((f) => f.status !== "failed")
  const selKey = linked && choice.chosen ? choice.selectedIds.join(",") : ""
  // The combined order.draft allowance when the plan has one; the legacy per-order metric for a grandfathered Pro.
  const hasOrderDraft = useMeStore((s) => !!s.me?.entitlements?.metrics.some((m) => m.metric === "order.draft"))
  const metric: QuotaMetric = hasOrderDraft ? "order.draft" : sim.metric
  const planState = useMeStore((s) => (s.me?.entitlements ? planStateOf(s.me.entitlements) : null))
  const freeTier = planState === "free" || planState === "free_month" || planState === "plus"
  const [sampling, setSampling] = useState(false)

  useEffect(() => {
    let live = true
    caseLookup.then((c) => { if (live) setLinked(c) })
    return () => { live = false }
  }, [caseLookup])

  useEffect(() => {
    let live = true
    Promise.all([oioApi.listThreads(6).catch(() => []), oiaApi.listThreads(6).catch(() => [])]).then(([a, b]) => {
      if (!live) return
      const rows = [...(a as Recent[]).map((r) => ({ ...r, kind: "oio" as const })), ...(b as Recent[]).map((r) => ({ ...r, kind: "oia" as const }))]
      setRecent(rows.sort((x, y) => y.updated_at.localeCompare(x.updated_at)))
    })
    return () => { live = false }
  }, [])

  const choose = (next: SimKind) => {
    setKind(next)
    setSaid("")
    try { localStorage.setItem(LAST_KEY, next) } catch { /* the choice still holds for this visit */ }
    track("sim_door_selected", { sim: next })
  }

  const follow = (k: SimKind, id: string) => {
    setThreads((cur) => ({ ...cur, [k]: id }))
    if (k === "oio") void useOioDocs.getState().hydrate(id).catch(() => undefined)
    else void useOiaDocs.getState().hydrate(id).catch(() => undefined)
  }

  // The case is made with the first document, as a guided draft; the documents need it.
  const ensureThread = (k: SimKind = kind, fresh = false): Promise<string> => {
    if (threads[k] && !fresh) return Promise.resolve(threads[k])
    const made = making.current[k] ?? (async () => {
      const caseId = (await caseLookup)?.id
      const before = caseId ? kept(caseId, k) : ""
      // Not run yet (no events): the same draft, in its own mode, with whatever it already holds.
      const prior = before ? await SIM[k].api.getThread(before).catch(() => null) : null
      if (prior && prior.last_seq === 0 && !fresh) {
        madeWith.current[k] = "?"
        // The quick mode is no longer offered; a draft left in it goes back to guided.
        if (prior.mode !== "guided") await SIM[k].api.setMode(before, "guided").catch(() => undefined)
        follow(k, before)
        return before
      }
      // No title: the agent names the draft from the notice once it has read it.
      const mode = "guided"
      // Only this proceeding's checked documents, when the workspace has them sorted; else all of them.
      const picked = caseId && k === kind && selKey ? choice.selectedIds : undefined
      const row = picked ? await SIM[k].api.createThread("", mode, caseId, picked) : await SIM[k].api.createThread("", mode, caseId)
      madeWith.current[k] = picked ? selKey : ""
      if (caseId) keep(caseId, k, row.id)
      follow(k, row.id)
      return row.id
    })().catch((err) => { delete making.current[k]; throw err })
    making.current[k] = made
    return made
  }

  // Nothing is made on opening the page, Matter-linked or not: a draft exists after the first file
  // is accepted, or on Start (a Matter draft with nothing new dropped).

  // A first drop that every file of failed leaves nothing behind: the draft just made, with no
  // documents and no events, is deleted again.
  const discard = async (k: SimKind, id: string) => {
    try {
      const api = SIM[k].api
      const [t, d] = await Promise.all([api.getThread(id), api.listDocuments(id)])
      if (t.last_seq !== 0 || d.length) return
      await api.deleteThread(id)
      delete making.current[k]
      setThreads((cur) => ({ ...cur, [k]: "" }))
      const caseId = (await caseLookup)?.id
      if (caseId) keep(caseId, k, null)
    } catch { /* an empty draft that stays is hidden from the history */ }
  }

  const upload = async (list: File[] | null, via: "picker" | "drop" | "paste") => {
    const { files, message } = filterSimFiles(Array.from(list ?? []))
    setSaid(message)
    trackRefused(kind, message)
    if (!files.length) return
    setBusy("upload")
    try {
      const fresh = !threadId
      const id = await ensureThread()
      const store = kind === "oio" ? useOioDocs.getState() : useOiaDocs.getState()
      const failed: string[] = []
      const refused: string[] = []
      const went: File[] = []
      await Promise.all(files.map((f) => store.upload(id, f).then(() => { went.push(f) }).catch((err) => {
        const own = uploadRefusalMessage(err)
        if (own) refused.push(own)
        else failed.push(`${f.name} (${uploadReason(err)})`)
        trackUploadFailed(kind, uploadFailReason(err))
      })))
      trackUploaded(kind, went, via)
      const lines = [...(failed.length ? [`Couldn't add ${failed.join(", ")}.`] : []), ...refused]
      if (lines.length) setSaid(lines.join(" "))
      if (fresh && failed.length === files.length) await discard(kind, id)
    } catch {
      setSaid("Couldn't start the case. Try again.")
    } finally {
      setBusy("")
    }
  }

  usePasteFiles(dropZone, (files) => void upload(files, "paste"))

  // Start needs a draft with something to read: its own upload, or the chosen proceeding's files.
  const canStart = !!threadId || (!!linked && chosenFiles.length > 0)

  const draft = async () => {
    if (!canStart || busy) return
    // Order drafts are Pro's (one a month; Free and Plus have none): the paywall is on this press, before the
    // draft runs. The sample case below is always free.
    setGateLine("")
    setSaid("")
    const gate = await gateAction(metric)
    if (!gate.ok) { setGateLine(gate.line); return }
    setBusy("start")
    try {
      // A Matter draft with nothing new dropped is made now, on the press.
      let id = threadId
      // The selection changed after a draft was made, and the draft holds nothing of its own: make it again.
      if (id && linked && selKey && madeWith.current[kind] !== selKey && !docs.length) {
        await sim.api.deleteThread(id).catch(() => undefined)
        delete making.current[kind]
        id = await ensureThread(kind, true)
      }
      id = id || await ensureThread()
      const names = docs.filter((d) => d.status !== "failed").map((d) => d.filename)
      // The notice (or order) answered: the chosen proceeding's anchor, else the draft's own notice, else
      // any upload of the draft. A draft bound to a Matter always gets the Matter start message (the
      // agent reads the workspace through Nira); only a draft with no file at all names no target.
      const bound = !!linked
      const usableDocs = docs.filter((d) => d.status !== "failed")
      const own = bound
        ? usableDocs.find((d) => d.status === "ready" && docParts(d.summary, d.filename, kind).some((p) => p.kind === (kind === "oio" ? "scn" : "oio"))) ?? usableDocs[0]
        : undefined
      const anchor = choice.members.find((m) => m.file.doc_id === choice.anchor)
      const target = anchor?.file.name ?? own?.filename
      const named = choice.selectedIds.length && bound ? [
        ...choice.members.filter((m) => choice.isChecked(m.file.doc_id)).map((m) => ({ label: m.role === "scn" ? "SCN" : [m.label, m.date ? `(${shortDate(m.date)})` : ""].filter(Boolean).join(" "), name: m.file.name })),
        ...choice.extras.filter((x) => choice.isChecked(x.file.doc_id)).map((x) => ({ label: x.hint ? "Document" : "Evidence", name: x.file.name })),
      ] : undefined
      await sim.api.postTurn(id, sim.start(names, target, bound, named), startRequestId(id))
      if (linked) keep(linked.id, kind, null)
      track("sim_session_started", { sim: kind, guided: true })
      navigate(`${sim.base}/${encodeURIComponent(id)}`)
    } catch (err) {
      setBusy("")
      // A 402 has opened the paywall already (the gateway is the backstop); anything else is said here.
      if ((err as { status?: number })?.status !== 402) setSaid("Couldn't start the draft. Try again.")
    }
  }

  /** The bundled sample case: a finished run-through and draft for a made-up noticee. Free, never counted. */
  const trySample = async () => {
    if (sampling || busy) return
    setSampling(true)
    setSaid("")
    try {
      const row = await sim.api.createSample()
      navigate(`${sim.base}/${encodeURIComponent(row.id)}`)
    } catch {
      setSampling(false)
      setSaid("Couldn't open the sample case. Try again.")
    }
  }

  const blocked = !canStart || reading || gaps.some((g) => g.needed) || !!busy
  return (
    <div className="na2 na4 ncf nsl">
      <SimBanner />
      <NiraBar onMenu={() => setHistOpen((v) => !v)} menuOpen={histOpen} menuControls="nira-hlist"
        crumbs={linked
          ? [{ label: "Workspaces", to: "/workspaces" }, { label: linked.title, to: `/workspace/${encodeURIComponent(linked.id)}` }, { label: "Order simulator" }]
          : [{ label: "Home", to: "/ask" }, { label: "Order simulator" }]} />
      <Rail threadId="" histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly scope={kind} />
      <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
      <main className="nsl-frame">
        <div className="nsl-col">
          <div className="nsl-head">
            <p className="ncf-eyebrow">Order simulator</p>
            <h1>Which order? Then drop everything.</h1>
            <p className="nsl-read">
              Everything in one go: {sim.steps}. About {totalMinutes(kind)} minutes. Nira reads everything, asks you when something the order depends on is missing or unclear, and shows each stage for you to confirm.
            </p>
            {linked ? (
              <p className="nsl-case">
                For the workspace <Link to={`/case/${encodeURIComponent(linked.id)}`}>{linked.title}</Link>. The draft is listed there.
                {linked.files === null ? " Couldn't load the workspace's files. Drop them here." : null}
                {usable.length ? <> <b>{usingLine(linked.title, usable.length)}</b>. Drop anything else for this draft.</> : null}
              </p>
            ) : null}
          </div>
          <div className="nsl-kinds" role="radiogroup" aria-label="Which order">
            {(["oio", "oia"] as const).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k} className={`nsl-kind${kind === k ? " on" : ""}`} onClick={() => choose(k)}>
                <span className="ncf-eyebrow">{SIM[k].eyebrow}</span>
                <b>{SIM[k].title}</b>
                <span>{SIM[k].body}</span>
              </button>
            ))}
          </div>
          <div
            ref={dropZone}
            className={`nsl-drop${over ? " over" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={sim.drop}
            onClick={() => file.current?.click()}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); file.current?.click() } }}
            onDragOver={(e) => { e.preventDefault(); setOver(true) }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); void upload(e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : null, "drop") }}
          >
            <Beam state={busy === "upload" ? "working" : "idle"} label={null} />
            <b>{sim.drop}</b>
            <span>{sim.dropSub}</span>
            <span>or paste an image</span>
            <input ref={file} type="file" multiple hidden accept={SIM_ACCEPT} data-testid="sim-file-input"
              onChange={(e) => { const l = e.target.files; void upload(l ? Array.from(l) : null, "picker"); e.target.value = "" }} />
          </div>
          {linked ? (
            <div className="nsl-pick">
              <ReadingWait choice={choice} />
              <ProceedingRadios choice={choice} />
              <FilesChecklist choice={choice} />
            </div>
          ) : null}
          {(docs.length || threadId || usable.length) && (docs.length || (!reading && gaps.length)) ? (
            <ul className="nsl-docs" aria-label="Documents">
              {docs.map((d) => {
                const parts = docParts(d.summary, d.filename, kind)
                const ready = d.status === "ready"
                return (
                  <li key={d.docId}>
                    {ready ? parts.map((p, i) => <span key={i} className="nsl-chip mono">{CHIP[p.kind]}</span>) : <span className="nsl-chip mono">…</span>}
                    <span className="nsl-fn">{d.filename}</span>
                    {d.pagesTotal ? <span className="nsl-pg mono">{d.pagesTotal} {d.pagesTotal === 1 ? "page" : "pages"}</span> : null}
                    <span className={`nsl-ty${d.status === "failed" ? " bad" : ""}`}>{d.status === "failed" ? "could not be read" : ready ? parts.map((p) => [kindLabel(p.kind, kind), pagesLabel(p)].filter(Boolean).join(" · ")).join("; ") : "reading"}</span>
                  </li>
                )
              })}
              {!reading ? gaps.map((g) => (
                <li key={g.text} className={`gap${g.needed ? " need" : ""}`}>
                  <span className="nsl-chip mono dashed">{g.chip}</span>
                  <span className="nsl-fn">{g.text}</span>
                  <span className="nsl-ty">{g.then}</span>
                </li>
              )) : null}
            </ul>
          ) : null}
          {said ? <p className="ncf-said" role="status">{said}</p> : null}
          <div className="nsl-go">
            <button type="button" className="ncf-btn primary nsl-btn" disabled={blocked} aria-busy={busy === "start"} onClick={() => void draft()}>
              {busy === "start" ? "Starting…" : "Draft the order"}
            </button>
            <span className="nsl-tier">
              {reading ? "Reading the documents…" : freeTier ? "Order drafts come with Pro" : ""}
            </span>
            <AllowanceHint metric={metric} />
          </div>
          <GateLine text={gateLine} />
          <p className="nsl-sample">
            <button type="button" className="pro-link" disabled={sampling} onClick={() => void trySample()}>
              {sampling ? "Opening the sample case…" : "Try the sample case"}
            </button>
            <span> A finished draft for a made-up noticee, to read and edit. Always free.</span>
          </p>
        </div>
        <aside className="nsl-side">
          <p className="ncf-eyebrow">What Nira does, in order</p>
          <Ladder kind={kind} />
          {recent?.length ? (
            <div className="nsl-recent">
              <p className="ncf-eyebrow">Your recent drafts</p>
              {/* One list per order, the order chosen above first. */}
              {([kind, kind === "oio" ? "oia" : "oio"] as const).map((k) => {
                const rows = recent.filter((r) => r.kind === k).slice(0, 4)
                return rows.length ? (
                  <section key={k} className="nsl-recent-g" aria-label={`Recent ${SIM[k].eyebrow} drafts`}>
                    <h3>{SIM[k].eyebrow}</h3>
                    <ul>
                      {rows.map((r) => (
                        <li key={r.id}><Link to={`${SIM[k].base}/${encodeURIComponent(r.id)}`}><span>{r.title || "Untitled draft"}</span><span className="mono">{ago(r.updated_at)}</span></Link></li>
                      ))}
                    </ul>
                  </section>
                ) : null
              })}
            </div>
          ) : null}
        </aside>
      </main>
    </div>
  )
}
